import { FactoryParams, ComputedProperty } from '@vue-storefront/core';
import { Ref, computed } from '@nuxtjs/composition-api';
import {
  sharedRef,
  Logger,
  configureFactoryParams
} from '@vue-storefront/core';
import {
  Context,
  CustomQuery,
  LoadPaymentDetailsListParams,
  LoadSopPaymentRequestProps,
  LoadSopPaymentResponseProps,
  RemovePaymentDetailsParams,
  UpdatePaymentDetailsParams
} from '../types';
import {
  PaymentDetails,
  PaymentRequest
} from '@vsf-enterprise/sap-commerce-webservices-sdk';

export interface UsePaymentFactoryParams extends FactoryParams {
  loadSopPaymentRequest: (
    context: Context,
    params: LoadSopPaymentRequestProps
  ) => Promise<PaymentRequest>;
  loadSopPaymentResponse: (
    context: Context,
    params: LoadSopPaymentResponseProps
  ) => Promise<PaymentDetails>;
  loadPaymentDetailsList(
    context: Context,
    params: LoadPaymentDetailsListParams
  ): Promise<PaymentDetails[]>;
  updatePaymentDetails(
    context: Context,
    params: UpdatePaymentDetailsParams
  ): Promise<PaymentDetails[]>;
  removePaymentDetails(
    context: Context,
    params: RemovePaymentDetailsParams
  ): Promise<PaymentDetails[]>;
}

export interface UsePaymentErrors {
  loadSopPaymentRequest: Error;
  loadSopPaymentResponse: Error;
  loadPaymentDetails: Error;
  updatePaymentDetails: Error;
  removePaymentDetails: Error;
}

export interface UsePayment {
  sopPaymentRequest: ComputedProperty<PaymentRequest>;
  sopPaymentResponse: ComputedProperty<PaymentDetails>;
  paymentDetailsList: ComputedProperty<PaymentDetails[]>;
  loadingSopPaymentRequest: ComputedProperty<boolean>;
  loadingSopPaymentResponse: ComputedProperty<boolean>;
  loadingPaymentDetailsList: ComputedProperty<boolean>;
  loadingUpdatePaymentDetails: ComputedProperty<boolean>;
  loadingRemovePaymentDetails: ComputedProperty<boolean>;
  error: ComputedProperty<UsePaymentErrors>;
  selectedPaymentDetailsId: ComputedProperty<string>;
  loadSopPaymentRequest(
    params: LoadSopPaymentRequestProps & { customQuery?: CustomQuery }
  ): Promise<void>;
  loadSopPaymentResponse(
    params: LoadSopPaymentResponseProps & { customQuery?: CustomQuery }
  ): Promise<void>;
  loadPaymentDetailsList(params: LoadPaymentDetailsListParams): Promise<void>;
  updatePaymentDetails(params: UpdatePaymentDetailsParams): Promise<void>;
  removePaymentDetails(params: RemovePaymentDetailsParams): Promise<void>;
  selectPaymentDetails(details: PaymentDetails): void;
  clearPaymentResponse(): void;
}

export function usePaymentFactory(factoryParams: UsePaymentFactoryParams) {
  return function usePayment(id?: string): UsePayment {
    const sopPaymentRequest: Ref<PaymentRequest | null> = sharedRef(
      null,
      `usePayment-sopPaymentRequest-${id}`
    );
    const sopPaymentResponse: Ref<PaymentDetails | null> = sharedRef(
      null,
      `usePayment-sopPaymentResponse-${id}`
    );
    const paymentDetailsList: Ref<PaymentDetails[] | null> = sharedRef(
      null,
      `usePayment-paymentDetailsList-${id}`
    );
    const loadingSopPaymentRequest: Ref<boolean> = sharedRef(
      false,
      `usePayment-sopPaymentRequest-loading-${id}`
    );
    const loadingSopPaymentResponse: Ref<boolean> = sharedRef(
      false,
      `usePayment-sopPaymentResponse-loading-${id}`
    );
    const loadingPaymentDetailsList: Ref<boolean> = sharedRef(
      false,
      `usePayment-paymentDetailsList-loading-${id}`
    );
    const loadingUpdatePaymentDetails: Ref<boolean> = sharedRef(
      false,
      `usePayment-updatePaymentDetails-loading-${id}`
    );
    const loadingRemovePaymentDetails: Ref<boolean> = sharedRef(
      false,
      `usePayment-removePaymentDetails-loading-${id}`
    );
    const selectedPaymentDetailsId = sharedRef(
      null,
      `usePayment-selectedPaymentDetailsId-${id}`
    );

    const _factoryParams = configureFactoryParams(factoryParams);
    const error: Ref<UsePaymentErrors> = sharedRef(
      {
        loadSopPaymentRequest: null,
        loadSopPaymentResponse: null
      },
      `usePayment-error-${id}`
    );

    const loadSopPaymentRequest = async (
      props: LoadSopPaymentRequestProps & { customQuery?: CustomQuery }
    ) => {
      Logger.debug(`usePayment/${id}/loadSopPaymentRequest`, props);

      try {
        loadingSopPaymentRequest.value = true;
        sopPaymentRequest.value = await _factoryParams.loadSopPaymentRequest(
          props
        );
        error.value.loadSopPaymentRequest = null;
      } catch (err) {
        error.value.loadSopPaymentRequest = err;
        Logger.error(`usePayment/${id}/loadSopPaymentRequest`, err);
      } finally {
        loadingSopPaymentRequest.value = false;
      }
    };

    const loadSopPaymentResponse = async (
      props: LoadSopPaymentResponseProps & { customQuery?: CustomQuery }
    ) => {
      Logger.debug(`usePayment/${id}/loadSopPaymentResponse`, props);

      try {
        loadingSopPaymentResponse.value = true;
        sopPaymentResponse.value = await _factoryParams.loadSopPaymentResponse(
          props
        );
        error.value.loadSopPaymentRequest = null;
      } catch (err) {
        error.value.loadSopPaymentRequest = err;
        Logger.error(`usePayment/${id}/loadSopPaymentResponse`, err);
      } finally {
        loadingSopPaymentResponse.value = false;
      }
    };

    const selectPaymentDetails = (details) => {
      selectedPaymentDetailsId.value = details.id;
    };

    const selectDefaultPayment = () => {
      const defaultPaymentDetails = paymentDetailsList.value?.find(
        (e) => e.defaultPayment
      );
      if (defaultPaymentDetails) selectPaymentDetails(defaultPaymentDetails);
    };

    const loadPaymentDetailsList = async (
      props: LoadPaymentDetailsListParams
    ) => {
      Logger.debug(`usePayment/${id}/loadPaymentDetailsList`, props);

      try {
        loadingPaymentDetailsList.value = true;
        paymentDetailsList.value = await _factoryParams.loadPaymentDetailsList(
          props
        );
        error.value.loadPaymentDetails = null;
        selectDefaultPayment();
      } catch (err) {
        error.value.loadPaymentDetails = err;
        Logger.error(`usePayment/${id}/loadPaymentDetails`, err);
      } finally {
        loadingPaymentDetailsList.value = false;
      }
    };

    const updatePaymentDetails = async (props: UpdatePaymentDetailsParams) => {
      Logger.debug(`usePayment/${id}/updatePaymentDetails`, props);

      try {
        loadingUpdatePaymentDetails.value = true;
        paymentDetailsList.value = await _factoryParams.updatePaymentDetails(
          props
        );
        error.value.updatePaymentDetails = null;
      } catch (err) {
        error.value.updatePaymentDetails = err;
        Logger.error(`usePayment/${id}/updatePaymentDetails`, err);
      } finally {
        loadingUpdatePaymentDetails.value = false;
      }
    };

    const removePaymentDetails = async (props: RemovePaymentDetailsParams) => {
      Logger.debug(`usePayment/${id}/removePaymentDetails`, props);

      try {
        loadingRemovePaymentDetails.value = true;
        paymentDetailsList.value = await _factoryParams.removePaymentDetails(
          props
        );

        if (props.paymentDetailsId === selectedPaymentDetailsId.value) {
          selectedPaymentDetailsId.value = null;
        }

        error.value.removePaymentDetails = null;
      } catch (err) {
        error.value.removePaymentDetails = err;
        Logger.error(`usePayment/${id}/removePaymentDetails`, err);
      } finally {
        loadingRemovePaymentDetails.value = false;
      }
    };

    const clearPaymentResponse = () => {
      sopPaymentResponse.value = null;
    };

    return {
      loadSopPaymentRequest,
      loadSopPaymentResponse,
      loadPaymentDetailsList,
      updatePaymentDetails,
      removePaymentDetails,
      selectPaymentDetails,
      clearPaymentResponse,
      sopPaymentRequest: computed(() => sopPaymentRequest.value),
      sopPaymentResponse: computed(() => sopPaymentResponse.value),
      loadingSopPaymentRequest: computed(() => loadingSopPaymentRequest.value),
      loadingSopPaymentResponse: computed(
        () => loadingSopPaymentResponse.value
      ),
      loadingPaymentDetailsList: computed(
        () => loadingPaymentDetailsList.value
      ),
      loadingRemovePaymentDetails: computed(
        () => loadingRemovePaymentDetails.value
      ),
      loadingUpdatePaymentDetails: computed(
        () => loadingUpdatePaymentDetails.value
      ),
      paymentDetailsList: computed(() => paymentDetailsList.value),
      error: computed(() => error.value),
      selectedPaymentDetailsId: computed(() => selectedPaymentDetailsId.value)
    };
  };
}
